<template>
  <v-dialog
    :model-value="modelValue"
    fullscreen
    scrollable
    transition="dialog-bottom-transition"
    class="adoc-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="adoc-viewer" :class="{ 'adoc-viewer--dark': isDark }">
      <header class="adoc-header">
        <div class="adoc-header__left">
          <button type="button" class="adoc-header__menu" aria-label="Toggle navigation" @click="sidebarOpen = !sidebarOpen">
            <v-icon icon="mdi-menu" size="20" />
          </button>
          <div class="adoc-header__brand">
            <div class="adoc-header__brand-icon">
              <v-icon :icon="sourceMeta.icon" size="20" />
            </div>
            <div>
              <h1 class="adoc-header__title">{{ sourceMeta.title }}</h1>
              <p class="adoc-header__subtitle">{{ sourceMeta.subtitle }}</p>
            </div>
          </div>
        </div>

        <div class="adoc-header__right">
          <div class="adoc-header__search">
            <v-icon icon="mdi-magnify" size="18" class="adoc-header__search-icon" />
            <input
              v-model="searchQuery"
              type="search"
              class="adoc-header__search-input"
              placeholder="Search endpoints…"
              aria-label="Search documentation"
            />
            <span class="adoc-header__search-kbd">⌘K</span>
          </div>

          <button type="button" class="adoc-header__close" @click="close">
            <v-icon icon="mdi-close" size="18" />
            <span>Close</span>
          </button>
        </div>
      </header>

      <div class="adoc-body">
        <div v-if="loading" class="adoc-state">
          <v-progress-circular indeterminate color="primary" size="36" width="3" />
          <p>Loading documentation…</p>
        </div>

        <div v-else-if="error" class="adoc-state adoc-state--error">
          <v-icon icon="mdi-alert-circle-outline" size="34" color="error" />
          <p>{{ error }}</p>
          <v-btn variant="flat" class="ak-docs-launch__cta" @click="loadDocs">Try again</v-btn>
        </div>

        <template v-else>
          <div
            class="adoc-sidebar-wrap"
            :class="{ 'adoc-sidebar-wrap--open': sidebarOpen }"
          >
            <ApiDocsSidebar
              :navigation="navigation"
              :active-id="activeSectionId"
              :search-query="searchQuery"
              @navigate="selectSection"
              @close="close"
            />
          </div>

          <main ref="mainEl" class="adoc-main">
            <ApiDocsEndpointSection
              v-if="activeSection"
              :section="activeSection"
              :api-key="apiKey"
              :base-url="testBaseUrl"
              @copied="$emit('copied')"
              @copy-error="$emit('copy-error')"
            />
          </main>
        </template>
      </div>
    </div>
  </v-dialog>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { useTheme } from 'vuetify'
import { getApiDocsPublicBaseUrl } from '@/utils/apiDocsEnvironment'
import ApiDocsSidebar from '@/components/ApiKeys/docs/ApiDocsSidebar.vue'
import ApiDocsEndpointSection from '@/components/ApiKeys/docs/ApiDocsEndpointSection.vue'
import {
  API_DOC_SOURCES,
  loadApiDocumentation,
} from '@/utils/apiDocumentation'

defineOptions({ name: 'ApiDocsFullscreenViewer' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  docSource: { type: String, default: 'public' },
  // Needed by the endpoint tester -- it sends the organisation key with each
  // trial request, exactly as a real integration would.
  apiKey: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'copied', 'copy-error'])

const theme = useTheme()
const loading = ref(false)
const error = ref('')
const navigation = ref([])
const sections = ref([])
const activeSectionId = ref('overview')
const testBaseUrl = computed(() => getApiDocsPublicBaseUrl(
  typeof window !== 'undefined' ? window.location.hostname : '',
))
const searchQuery = ref('')
const sidebarOpen = ref(false)
const mainEl = ref(null)

const isDark = computed(() => theme.global.current.value.dark)

const sourceMeta = computed(() => {
  const source = API_DOC_SOURCES[props.docSource] || API_DOC_SOURCES.public
  return {
    ...source,
    icon: props.docSource === 'contact'
      ? 'mdi-card-account-mail-outline'
      : 'mdi-api',
  }
})

const activeSection = computed(() =>
  sections.value.find((section) => section.id === activeSectionId.value) ||
  sections.value[0] ||
  null,
)

function close() {
  emit('update:modelValue', false)
}

function toggleTheme() {
  theme.global.name.value = isDark.value ? 'light' : 'dark'
}

function selectSection(id) {
  activeSectionId.value = id
  sidebarOpen.value = false
  mainEl.value?.scrollTo({ top: 0, behavior: 'smooth' })
}

function onSearchHotkey(event) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    const input = document.querySelector('.adoc-header__search-input')
    input?.focus()
  }
}

async function loadDocs() {
  loading.value = true
  error.value = ''

  try {
    const doc = await loadApiDocumentation({
      force: true,
      source: props.docSource,
    })
    navigation.value = doc.navigation
    sections.value = doc.sections
    activeSectionId.value = doc.sections[0]?.id ?? 'overview'
    searchQuery.value = ''
  } catch (err) {
    error.value = err?.message || 'Failed to load API documentation.'
  } finally {
    loading.value = false
  }
}

watch(
  () => [props.modelValue, props.docSource],
  ([open]) => {
    if (open) {
      loadDocs()
      window.addEventListener('keydown', onSearchHotkey)
      return
    }
    window.removeEventListener('keydown', onSearchHotkey)
    searchQuery.value = ''
    sidebarOpen.value = false
  },
)

onUnmounted(() => {
  window.removeEventListener('keydown', onSearchHotkey)
})
</script>
